"use client";

import { cn } from "@scott/ui/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import type React from "react";
import { Label } from "../label";
import "react-phone-input-2/lib/plain.css";
import PhoneInput from "react-phone-input-2";

export const inputVariants = cva("w-full focus:outline-none", {
  variants: {
    size: {
      default: "h-12",
      sm: "h-[30px]",
    },
  },
  defaultVariants: {
    size: "default",
  },
});

export interface PhoneFieldProps extends VariantProps<typeof inputVariants> {
  label?: string | React.ReactNode;
  value?: string;
  onChange: (
    value: string,
    country: Record<string, unknown>,
    e: React.ChangeEvent<HTMLInputElement>,
    formattedValue: string
  ) => void;
  disabled?: boolean;
  readOnly?: boolean;
  size?: "default" | "sm";
  name?: string;
  required?: boolean;
}

const PhoneNumberInput: React.FC<PhoneFieldProps> = ({
  label,
  value,
  onChange,
  disabled,
  readOnly,
  size = "default",
  name = "phoneNumber",
  required = false,
}) => {
  const inputClass = cn(
    "flex flex-1 basis-[100%] justify-self-start rounded-none px-3 py-4 text-neutral-darker ring-offset-background file:border-0 file:bg-transparent file:font-medium file:text-sm placeholder:text-neutral-medium read-only:cursor-not-allowed read-only:bg-neutral-light read-only:text-neutral-medium focus:outline-none disabled:cursor-not-allowed disabled:text-neutral-medium",
    size === "sm" ? "text-sm" : "text-md"
  );
  const containerClass = cn(
    "peer flex border border-neutral-medium hover:border-neutral-dark read-only:hover:border-neutral-medium disabled:bg-neutral-light"
  );
  const buttonClass = "";

  return (
    <div className={cn("flex w-full flex-1 flex-col gap-1")}>
      {label && (
        <Label
          className={cn(
            "py-1 font-bold text-sm",
            disabled && "text-neutral-medium",
            readOnly && "text-neutral-medium"
          )}
          htmlFor={name}
        >
          {label}
        </Label>
      )}

      <div>
        <PhoneInput
          buttonClass={buttonClass}
          containerClass={containerClass}
          country="us"
          dropdownClass="!z-50"
          inputClass={inputClass}
          inputProps={{
            name,
            required,
            readOnly,
            disabled,
            placeholder: "Mobile Number",
          }}
          onChange={onChange}
          value={value}
        />
      </div>
    </div>
  );
};

export { PhoneNumberInput };
